<template>
  <main class="page schedule-page">
    <page-header title="日程" subtitle="跟进与预计来访">
      <button class="icon-button" type="button" aria-label="刷新日程" :disabled="loading || summaryLoading" @click="refresh"><app-icon name="refresh" /></button>
      <button class="icon-button solid" type="button" aria-label="新增安排" @click="arrange"><app-icon name="plus" /></button>
    </page-header>

    <section class="schedule-calendar" aria-label="日程日历">
      <div class="cal-head">
        <button class="cal-nav" type="button" aria-label="上个月" :disabled="month === '1900-01'" @click="shiftMonth(-1)"><app-icon name="back" /></button>
        <button class="schedule-month" type="button" aria-label="选择日程日期" @click="dateOpen = true">{{ monthLabel }}<app-icon name="down" /></button>
        <button class="cal-nav" type="button" aria-label="下个月" :disabled="month === '2199-12'" @click="shiftMonth(1)"><app-icon name="arrow" /></button>
      </div>
      <div class="cal-week"><span v-for="name in weekNames" :key="name">{{ name }}</span></div>
      <div class="cal-grid schedule-grid">
        <button v-for="cell in cells" :key="cell.date" type="button" class="cal-day schedule-day" :class="{ muted: !cell.inMonth, today: cell.date === todayText, on: cell.date === selectedDay }" :disabled="!valid(cell.date)" :data-date="cell.date" :aria-label="dayLabel(cell)" :aria-pressed="cell.date === selectedDay ? 'true' : 'false'" @click="selectDay(cell.date)">
          <span>{{ cell.day }}</span>
          <span class="schedule-day-marks"><i v-if="hasType(cell.date, 'follow')" class="schedule-dot follow" /><i v-if="hasType(cell.date, 'visit')" class="schedule-dot visit" /><small v-if="dayCount(cell.date)">{{ dayCount(cell.date) > 99 ? '99+' : dayCount(cell.date) }}</small></span>
        </button>
      </div>
      <div class="schedule-calendar-foot"><div class="schedule-legend"><span><i class="schedule-dot follow" />跟进</span><span><i class="schedule-dot visit" />预计来访</span></div><button type="button" class="text-button" :disabled="selectedDay === todayText" @click="selectDay(todayText)">回到今天</button></div>
    </section>

    <div v-if="summaryError" class="error-message">{{ summaryError }}<button type="button" class="text-button" @click="loadSummary">重试数量</button></div>
    <p class="schedule-month-total">{{ summaryLoading ? '正在读取本月安排…' : monthTotalText }}</p>
    <div class="seg schedule-filter" role="group" aria-label="日程类型"><button v-for="item in filters" :key="item.value" type="button" :class="{ on: type === item.value }" :aria-pressed="type === item.value ? 'true' : 'false'" @click="selectType(item.value)">{{ item.label }}</button></div>
    <div class="sec-head schedule-list-head"><h2>{{ selectedLabel }}</h2><span>{{ loading && !events.length ? '读取中' : total + ' 项安排' }}</span></div>

    <div v-if="error" class="error-message">{{ error }}<button type="button" class="text-button" @click="loadEvents(false)">重试</button></div>
    <van-list v-model="loading" :finished="finished || !!error" @load="loadEvents(false)">
      <div v-if="events.length" class="c-list schedule-events">
        <article v-for="event in events" :key="event.id" class="c-row schedule-event">
          <button class="c-main" type="button" @click="open(event)"><span class="c-when schedule-time" :class="{ late: selectedDay < todayText }"><strong>{{ eventTime(event) }}</strong><small :class="event.type">{{ event.type === 'visit' ? '来访' : '跟进' }}</small></span><span class="c-text"><span class="c-need">{{ need(event.customer) }}</span><span class="c-meta"><b>{{ event.customer.name || '未填写姓名' }}</b>{{ customerMeta(event.customer) }}</span></span></button>
          <a v-if="callable(event.customer)" class="c-dial" :href="'tel:' + event.customer.mobile" :aria-label="'拨打' + (event.customer.name || '客户')"><app-icon name="phone" /></a><span v-else class="c-dial off" title="联系方式受权限保护"><app-icon name="phone" /></span>
        </article>
      </div>
    </van-list>
    <div v-if="!loading && !error && finished && !events.length" class="empty-state schedule-empty"><app-icon name="calendar" /><strong>{{ emptyText }}</strong><p>{{ type === 'visit' ? '选择客户，记录约定并安排预计来访时间。' : '选择客户，记录沟通并安排下一次联系。' }}</p><button class="button small soft" type="button" @click="arrange">选择客户安排<app-icon name="plus" /></button></div>
    <p v-if="finished && events.length" class="schedule-footnote">按客户当前的跟进与预计来访安排展示</p>
    <datetime-sheet :show="dateOpen" title="选择日程日期" :value="selectedDay" @input="selectDay" @close="dateOpen = false" />
  </main>
</template>
<script>
import DatetimeSheet from '../../components/datetime-sheet'
import {
  today,
  parseDay,
  dayString,
  weekday,
  clock,
  needText,
  tail,
  projectText
} from '../../common/when'
import { canCall } from '../../common/customer'
import { validDay, scheduleType, scheduleLocation } from '../../common/schedule'
export default {
  components: { DatetimeSheet },
  data() {
    const cache = this.$store.state.views.schedule || {}
    const date = validDay(this.$route.query.date) || validDay(cache.date) || today()
    const type = scheduleType(this.$route.query.type || cache.type)
    return {
      selectedDay: date,
      type,
      todayText: today(),
      days: {},
      summaryMonth: '',
      totals: {},
      summaryLoading: false,
      summaryError: '',
      events: [],
      total: 0,
      page: 1,
      loading: false,
      fetching: false,
      finished: false,
      error: '',
      dateOpen: false,
      eventRequest: 0,
      summaryRequest: 0,
      weekNames: ['一', '二', '三', '四', '五', '六', '日'],
      filters: [
        { value: 'all', label: '全部' },
        { value: 'follow', label: '跟进' },
        { value: 'visit', label: '预计来访' }
      ]
    }
  },
  computed: {
    month() {
      return this.selectedDay.slice(0, 7)
    },
    monthLabel() {
      const date = parseDay(this.selectedDay)
      return date.getFullYear() + ' 年 ' + (date.getMonth() + 1) + ' 月'
    },
    selectedLabel() {
      const date = parseDay(this.selectedDay)
      return (
        (this.selectedDay === this.todayText
          ? '今天'
          : date.getMonth() + 1 + '月' + date.getDate() + '日') +
        ' ' +
        weekday(this.selectedDay)
      )
    },
    cells() {
      const date = parseDay(this.selectedDay)
      const first = new Date(date.getFullYear(), date.getMonth(), 1)
      const offset = (first.getDay() + 6) % 7
      const length = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()
      const count = Math.ceil((offset + length) / 7) * 7
      return Array.from({ length: count }, (_, index) => {
        const cell = new Date(first)
        cell.setDate(1 - offset + index)
        return {
          date: dayString(cell),
          day: cell.getDate(),
          inMonth: cell.getMonth() === date.getMonth()
        }
      })
    },
    monthTotalText() {
      if (this.summaryMonth !== this.month) return '选择日期查看安排'
      const count = this.totals[this.type === 'all' ? 'total' : this.type] || 0
      return (
        '本月 ' +
        count +
        ' 项' +
        (this.type === 'follow' ? '跟进' : this.type === 'visit' ? '预计来访' : '安排')
      )
    },
    emptyText() {
      return this.type === 'follow'
        ? '这一天没有跟进安排'
        : this.type === 'visit' ? '这一天没有预计来访' : '这一天没有安排'
    }
  },
  watch: {
    '$route.query'(query) {
      const day = validDay(query.date)
      const type = scheduleType(query.type)
      if (day && (day !== this.selectedDay || type !== this.type))
        this.setSelection(day, type, false)
    },
    '$store.state.revision': 'refresh',
    '$store.state.customerRevision': 'refresh'
  },
  created() {
    if (this.$route.query.date !== this.selectedDay || this.$route.query.type !== this.type)
      this.$router.replace(scheduleLocation(this.selectedDay, this.type))
    this.loadSummary()
    this.loadEvents(true)
  },
  beforeRouteLeave(to, from, next) {
    this.$store.state.views.schedule = { date: this.selectedDay, type: this.type }
    next()
  },
  methods: {
    valid: validDay,
    need: needText,
    callable(customer) {
      return canCall(customer.mobile)
    },
    eventTime(event) {
      return clock(event.scheduled_at) || '待定'
    },
    customerMeta(customer) {
      return [
        tail(customer.mobile),
        customer.labels_text ? customer.labels_text + '级' : '',
        projectText(customer.subject_text)
      ]
        .filter(Boolean)
        .map(text => ' · ' + text)
        .join('')
    },
    hasType(date, type) {
      return (
        this.summaryMonth === date.slice(0, 7) &&
        (this.type === 'all' || this.type === type) &&
        this.days[date] &&
        this.days[date][type] > 0
      )
    },
    dayCount(date) {
      const data = this.summaryMonth === date.slice(0, 7) && this.days[date]
      return data ? data[this.type === 'all' ? 'total' : this.type] : 0
    },
    dayLabel(cell) {
      return (
        cell.date +
        (cell.date === this.todayText ? '，今天' : '') +
        (this.summaryMonth === cell.date.slice(0, 7)
          ? '，' + (this.dayCount(cell.date) || 0) + '项安排'
          : '')
      )
    },
    selectDay(day) {
      if (!validDay(day) || day === this.selectedDay) return
      this.setSelection(day, this.type, true)
    },
    selectType(type) {
      if (type === this.type) return
      this.setSelection(this.selectedDay, type, true)
    },
    setSelection(day, type, updateUrl) {
      const previous = this.month
      this.selectedDay = day
      this.type = type
      if (updateUrl) this.$router.replace(scheduleLocation(day, type))
      if (previous !== this.month) this.loadSummary()
      this.loadEvents(true)
    },
    shiftMonth(step) {
      const date = parseDay(this.selectedDay)
      const month = new Date(date.getFullYear(), date.getMonth() + step, 1)
      const max = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate()
      month.setDate(Math.min(date.getDate(), max))
      this.selectDay(dayString(month))
    },
    refresh() {
      this.todayText = today()
      this.loadSummary()
      this.loadEvents(true)
    },
    async loadSummary() {
      const month = this.month
      const request = ++this.summaryRequest
      this.summaryLoading = true
      this.summaryError = ''
      try {
        const data = await this.$api.post('/api/employee/schedule/summary', { month })
        if (this._isDestroyed || request !== this.summaryRequest) return
        const days = {}
        ;(data.days || []).forEach(day => {
          days[day.date] = day
        })
        this.days = days
        this.totals = data.totals || {}
        this.summaryMonth = month
      } catch (e) {
        if (request === this.summaryRequest)
          this.summaryError = '日历数量读取失败，仍可选择日期查看安排'
      } finally {
        if (request === this.summaryRequest) this.summaryLoading = false
      }
    },
    async loadEvents(reset) {
      if (!reset && (this.fetching || this.finished)) return
      if (reset) {
        this.eventRequest++
        this.events = []
        this.total = 0
        this.page = 1
        this.finished = false
      }
      const request = this.eventRequest
      this.fetching = true
      this.loading = true
      this.error = ''
      try {
        const data = await this.$api.post('/api/employee/schedule/list', {
          date: this.selectedDay,
          type: this.type,
          page: this.page
        })
        if (this._isDestroyed || request !== this.eventRequest) return
        const pagination = data.meta.pagination
        const known = {}
        this.events.forEach(event => {
          known[event.id] = true
        })
        this.events = this.events.concat((data.data || []).filter(event => !known[event.id]))
        this.total = pagination.total
        this.finished = pagination.current_page >= pagination.total_pages
        this.page++
      } catch (e) {
        if (request === this.eventRequest) this.error = e.message
      } finally {
        if (request === this.eventRequest) {
          this.fetching = false
          this.loading = false
        }
      }
    },
    open(event) {
      this.$router.push({
        path: '/customer/detail/' + event.customer.id + '/schedule',
        query: { date: this.selectedDay, type: this.type }
      })
    },
    arrange() {
      const date = this.selectedDay < this.todayText ? this.todayText : this.selectedDay
      this.$router.push({
        path: '/customer',
        query: { schedule_date: date, schedule_type: this.type === 'visit' ? 'visit' : 'follow' }
      })
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/pages/schedule/index.vue